import type { QuoteItemInput } from './api';
import { WORKFLOW_METIN as M } from './metin/workflow';
import { SATIS_UI as S } from './metin/satis-ui';

export function paraAltBirime(raw: string): number {
  const temiz = raw.trim().replace(/\s/g, '');
  if (temiz === '') throw new Error(M.genel.tutarBosUyarisi);
  const eslesme = /^(-?)(\d+)(?:[.,](\d{1,2}))?$/.exec(temiz);
  if (!eslesme) throw new Error(M.genel.tutarBicimiUyarisi);
  const [, isaret, tamKisim, ondalik = ''] = eslesme;
  const kurus = `${ondalik}00`.slice(0, 2);
  const deger = Number.parseInt(tamKisim!, 10) * 100 + Number.parseInt(kurus, 10);
  return isaret === '-' ? -deger : deger;
}


export type DiscountMode = 'AMOUNT' | 'PERCENT';
const LIMIT = 2_000_000_000;
function valid(value: number): number {
  if (!Number.isSafeInteger(value) || value < 0 || value > LIMIT) throw new Error(S.invalidAdjustment);
  return value;
}
function amount(text: string): number { return valid(paraAltBirime(text.trim() || '0')); }
export function percentage(base: number, text: string): number {
  const basisPoints = amount(text);
  if (basisPoints > 10000) throw new Error(S.invalidPercentage);
  return valid(Number((BigInt(valid(base)) * BigInt(basisPoints) + 5000n) / 10000n));
}
function discount(base: number, text: string, mode: DiscountMode): number {
  const value = mode === 'PERCENT' ? percentage(base, text) : amount(text);
  if (value > base) throw new Error(S.discountExceedsBase);
  return value;
}
/**
 * Teklif vergisi seçimi (08.10.2026). `LEGACY`: kalem başına vergi yüzdesi, indirimli tutara biner
 * ve üstüne eklenir (eski davranış, varsayılan). Diğer üçü sürüm düzeyindedir ve sunucudaki
 * `api/src/common/workflow/quote-tax.ts` ile aynı hesabı yapar: KDV indirimden ÖNCE hesaplanır,
 * indirim KDV eklendikten sonraki tutardan düşer (Ürün Sahibi 04.10.2026).
 */
export type TaxChoice = 'LEGACY' | 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE';
export const DEFAULT_TAX_RATE_BPS = 500;

function divRound(numerator: number, denominator: number): number {
  return Number((BigInt(numerator) * 2n + BigInt(denominator)) / (BigInt(denominator) * 2n));
}
/** Kalem toplamından (indirimsiz) KDV tutarı. EXCLUSIVE: üstüne; INCLUSIVE: içinden ayrılan. */
export function quoteTaxOf(subtotalMinor: number, mode: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE', rateBps: number): number {
  if (mode === 'EXCLUSIVE') return divRound(subtotalMinor * rateBps, 10_000);
  if (mode === 'INCLUSIVE') return subtotalMinor - divRound(subtotalMinor * 10_000, 10_000 + rateBps);
  return 0;
}

export interface QuoteTaxDraft {
  taxMode: Exclude<TaxChoice, 'LEGACY'>; taxRateBps: number; taxMinor: number; subtotalMinor: number; grossMinor: number; totalMinor: number;
  /** Kalem bazlı yolda (C sınıfı) doldurulur: fiyatın üstüne eklenen ve içinden ayrılan KDV. */
  exclusiveTaxMinor: number; inclusiveTaxMinor: number;
  /** Ekranda gösterilecek özet kip: tek kip ya da karışık. Oran 0 olan kalem vergisiz sayılır. */
  displayMode: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE' | 'MIXED';
  /** Kalem bazlı yolda her kalemin sonucu; yoksa `undefined` (sürüm düzeyi tek hesap). */
  lines?: { mode: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE'; rateBps: number; taxMinor: number; grossMinor: number }[];
}

/** Taxes use discounted product amounts; fees are separate. API amounts remain integer minor units. */
export function priceQuoteDraft(
  items: QuoteItemInput[], fields: Record<string, string>,
  overall: { discount: string; discountMode: DiscountMode; fee: string },
  tax: { choice: TaxChoice; rate: string } = { choice: 'LEGACY', rate: '5' },
): { items: QuoteItemInput[]; discountMinor: number; tax?: QuoteTaxDraft } {
  if (tax.choice !== 'LEGACY') return priceWithQuoteTax(items, fields, overall, tax.choice, tax.rate);
  const priced = items.map((item, index) => {
    if (item.itemType === 'HOTEL' && item.productSlug && !item.hotelSelection) throw new Error(S.hotelSelectionRequired);
    if (item.hotelSelection && (!item.hotelSelection.expiresOn || Date.parse(item.hotelSelection.expiresOn) <= Date.now())) throw new Error(S.hotelSelectionExpired);
    const read = (key: string, fallback: number) => fields[`${index}:${key}`] === undefined
      ? valid(fallback) : amount(fields[`${index}:${key}`]!);
    const priceText = fields[`${index}:unitPriceMinor`];
    const unitPriceMinor = priceText === undefined ? valid(item.unitPriceMinor) : valid(paraAltBirime(priceText));
    if (!Number.isSafeInteger(item.quantity) || item.quantity < 1) throw new Error(S.invalidAdjustment);
    const base = valid(unitPriceMinor * item.quantity);
    const mode = fields[`${index}:discountMode`] === 'PERCENT' ? 'PERCENT' : 'AMOUNT';
    const rawDiscount = fields[`${index}:discountMinor`];
    const discountMinor = rawDiscount === undefined ? valid(item.discountMinor ?? 0) : discount(base, rawDiscount, mode);
    if (discountMinor > base) throw new Error(S.discountExceedsBase);
    // Devlet harcı yalnız vizede anlamlı; satış tutarının içindedir ve KDV matrahına girmez.
    const governmentFeeMinor = item.itemType === 'VISA' ? read('governmentFeeMinor', item.governmentFeeMinor ?? 0) : 0;
    if (governmentFeeMinor > base - discountMinor) throw new Error(S.governmentFeeExceeds);
    return { ...item, unitPriceMinor, discountMinor, feeMinor: read('feeMinor', item.feeMinor ?? 0), governmentFeeMinor };
  });
  const bases = priced.map(item => valid(item.quantity * item.unitPriceMinor - item.discountMinor));
  const base = valid(bases.reduce((sum, value) => sum + value, 0));
  const discountMinor = discount(base, overall.discount, overall.discountMode);
  // Allocate the overall discount cumulatively so every minor unit is allocated exactly once.
  let cumulativeBase = 0, allocated = 0;
  const result = priced.map((item, index) => {
    cumulativeBase += bases[index]!;
    const cumulative = base === 0 ? 0 : Number((BigInt(discountMinor) * BigInt(cumulativeBase) + BigInt(Math.floor(base / 2))) / BigInt(base));
    const taxable = Math.max(0, bases[index]! - (cumulative - allocated) - item.governmentFeeMinor);
    allocated = cumulative;
    const rate = fields[`${index}:taxRate`];
    const taxMinor = rate === undefined ? valid(item.taxMinor ?? 0) : percentage(taxable, rate);
    return { ...item, taxMinor };
  });
  if (result[0]) result[0] = { ...result[0], feeMinor: valid(result[0].feeMinor + amount(overall.fee)) };
  return { items: result, discountMinor };
}

/** Recover editable percentage rates only when they reproduce the saved tax exactly. */
export function revisionTaxFields(items: QuoteItemInput[], overallDiscount: number): Record<string, string> {
  const bases = items.map(item => item.quantity * item.unitPriceMinor - (item.discountMinor ?? 0));
  const total = bases.reduce((sum, value) => sum + value, 0);
  let cumulativeBase = 0, allocated = 0;
  const fields: Record<string, string> = {};
  items.forEach((item, index) => {
    cumulativeBase += bases[index]!;
    const cumulative = total === 0 ? 0 : Number((BigInt(overallDiscount) * BigInt(cumulativeBase) + BigInt(Math.floor(total / 2))) / BigInt(total));
    const taxable = Math.max(0, bases[index]! - (cumulative - allocated) - (item.governmentFeeMinor ?? 0));
    allocated = cumulative;
    const tax = item.taxMinor ?? 0;
    if (tax === 0) fields[`${index}:taxRate`] = '0';
    else if (taxable > 0) {
      const rate = (Math.round(tax * 10000 / taxable) / 100).toFixed(2);
      if (Number(rate) <= 100 && percentage(taxable, rate) === tax) fields[`${index}:taxRate`] = rate;
    }
  });
  return fields;
}

const KIPLER = ['NONE', 'EXCLUSIVE', 'INCLUSIVE'] as const;
type Kip = (typeof KIPLER)[number];

/**
 * Kalem bazlı vergi (09.10.2026). Kalemde `${i}:vergiKipi` ve `${i}:vergiOrani` varsa o kalem kendi kipini ve oranını
 * taşır; yoksa sürümün (varsayılan) kipini ve oranını miras alır. Sunucudaki `computeMixedQuoteTax` ile aynı hesap:
 * hiçbir kalemde açık kip/oran yoksa eski tek toplam hesabı (tek yuvarlama), varsa her kalem ayrı yuvarlanır ve belge
 * KDV'si kalem KDV'lerinin toplamıdır. Devlet harcı hiçbir kipte matrah değildir.
 */
function priceWithQuoteTax(
  items: QuoteItemInput[], fields: Record<string, string>,
  overall: { discount: string; discountMode: DiscountMode; fee: string },
  choice: Exclude<TaxChoice, 'LEGACY'>, rateText: string,
): { items: QuoteItemInput[]; discountMinor: number; tax: QuoteTaxDraft } {
  // Kalem yüzdesi kullanılmaz (sunucu çift vergiyi reddeder); kalem vergisi 0'a sabitlenir.
  const base = priceQuoteDraft(items, { ...fields, ...Object.fromEntries(items.map((_, i) => [`${i}:taxRate`, '0'])) }, { ...overall, discount: '0', discountMode: 'AMOUNT' });
  const typedRateBps = amount(rateText.trim() || '0');
  if (typedRateBps > 10000) throw new Error(S.invalidPercentage);
  const defaultRateBps = choice === 'NONE' ? 0 : typedRateBps;
  const lines = base.items.map((item, index) => {
    const kipRaw = fields[`${index}:vergiKipi`];
    const kipOverride: Kip | undefined = (KIPLER as readonly string[]).includes(kipRaw ?? '') && kipRaw !== choice ? (kipRaw as Kip) : undefined;
    const oranRaw = fields[`${index}:vergiOrani`];
    const oranBps = oranRaw !== undefined && oranRaw.trim() !== '' ? amount(oranRaw) : undefined;
    if (oranBps !== undefined && oranBps > 10000) throw new Error(S.invalidPercentage);
    const rateOverride = kipOverride !== undefined || (oranBps !== undefined && oranBps !== defaultRateBps)
      ? (oranBps ?? typedRateBps) : undefined;
    return { ...item, taxMinor: 0, ...(kipOverride ? { taxMode: kipOverride } : {}), ...(rateOverride !== undefined ? { taxRateBps: rateOverride } : {}) };
  });
  const lineTotal = (item: QuoteItemInput) => item.quantity * item.unitPriceMinor + (item.feeMinor ?? 0) - (item.discountMinor ?? 0);
  const subtotalMinor = valid(lines.reduce((sum, item) => sum + lineTotal(item), 0));
  const explicit = lines.some(item => item.taxMode !== undefined || item.taxRateBps !== undefined);
  // KDV matrahı = satış - devlet harcı (04.10.2026); harç girilmediyse 0 ve davranış aynen kalır.
  const governmentFeeMinor = valid(lines.reduce((sum, item) => sum + (item.governmentFeeMinor ?? 0), 0));
  let taxMinor: number, grossMinor: number, exclusiveTaxMinor = 0, inclusiveTaxMinor = 0;
  let perLine: QuoteTaxDraft['lines'];
  let displayMode: QuoteTaxDraft['displayMode'];
  if (!explicit) {
    taxMinor = quoteTaxOf(subtotalMinor - governmentFeeMinor, choice, defaultRateBps);
    grossMinor = choice === 'EXCLUSIVE' ? subtotalMinor + taxMinor : subtotalMinor;
    if (choice === 'EXCLUSIVE') exclusiveTaxMinor = taxMinor; else if (choice === 'INCLUSIVE') inclusiveTaxMinor = taxMinor;
    displayMode = choice !== 'NONE' && defaultRateBps === 0 ? 'NONE' : choice;
  } else {
    perLine = lines.map(item => {
      const mode: Kip = item.taxMode ?? choice;
      const rateBps = item.taxRateBps ?? defaultRateBps;
      const total = lineTotal(item);
      const tax = quoteTaxOf(total - (item.governmentFeeMinor ?? 0), mode, rateBps);
      return { mode, rateBps, taxMinor: tax, grossMinor: mode === 'EXCLUSIVE' ? total + tax : total };
    });
    taxMinor = perLine.reduce((sum, line) => sum + line.taxMinor, 0);
    grossMinor = perLine.reduce((sum, line) => sum + line.grossMinor, 0);
    exclusiveTaxMinor = perLine.reduce((sum, line) => sum + (line.mode === 'EXCLUSIVE' ? line.taxMinor : 0), 0);
    inclusiveTaxMinor = perLine.reduce((sum, line) => sum + (line.mode === 'INCLUSIVE' ? line.taxMinor : 0), 0);
    // Vergisiz (ya da oranı 0) kalemler özet kipini belirlemez: geri kalanlar tek kip ve tek oransa o kip, aksi hâlde karışık.
    const taxed = perLine.filter(line => line.mode !== 'NONE' && line.rateBps !== 0);
    const kinds = new Set(taxed.map(line => line.mode));
    const rates = new Set(taxed.map(line => line.rateBps));
    displayMode = taxed.length === 0 ? 'NONE' : kinds.size === 1 && rates.size === 1 ? [...kinds][0]! : 'MIXED';
  }
  // İndirim KDV'li tutardan düşer; yüzde modunda da yüzde KDV'li tutarın yüzdesidir.
  const discountMinor = discount(grossMinor, overall.discount, overall.discountMode);
  return {
    items: lines, discountMinor,
    tax: { taxMode: choice, taxRateBps: defaultRateBps, taxMinor, subtotalMinor, grossMinor, totalMinor: grossMinor - discountMinor, exclusiveTaxMinor, inclusiveTaxMinor, displayMode, lines: perLine },
  };
}

/** Ürün kartında gösterilen KDV bilgisi: oran, hariç/dahil, tutar. */
export interface ItemTaxInfo { mode: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE'; rateBps: number | null; taxMinor: number; governmentFeeMinor: number }

/**
 * Her kalemin KDV'si, belgedeki KDV ile aynı hesaptan (yeni formül yok).
 * LEGACY: kalem yüzdesi, hariç (üstüne biner). EXCLUSIVE/INCLUSIVE: teklif oranı, belge KDV'si kalemlere
 * kümülatif dağıtılır ki kalem toplamı belge KDV'sine kuruşu kuruşuna eşit olsun. NONE: KDV yok.
 */
export function itemTaxInfos(
  items: QuoteItemInput[], fields: Record<string, string>,
  overall: { discount: string; discountMode: DiscountMode; fee: string },
  tax: { choice: TaxChoice; rate: string },
): ItemTaxInfo[] {
  if (items.length === 0) return [];
  const priced = priceQuoteDraft(items, fields, overall, tax);
  if (tax.choice === 'LEGACY') {
    return priced.items.map((item, index) => {
      const rateText = fields[`${index}:taxRate`];
      return { mode: 'EXCLUSIVE', rateBps: rateText === undefined ? null : amount(rateText.trim() || '0'), taxMinor: item.taxMinor ?? 0, governmentFeeMinor: item.governmentFeeMinor ?? 0 };
    });
  }
  const draft = priced.tax!;
  if (draft.lines) return draft.lines.map((line, index) => ({ mode: line.mode, rateBps: line.rateBps, taxMinor: line.taxMinor, governmentFeeMinor: priced.items[index]!.governmentFeeMinor ?? 0 }));
  if (draft.taxMode === 'NONE') return priced.items.map(item => ({ mode: 'NONE', rateBps: 0, taxMinor: 0, governmentFeeMinor: item.governmentFeeMinor ?? 0 }));
  const bases = priced.items.map(item => item.quantity * item.unitPriceMinor + (item.feeMinor ?? 0) - (item.discountMinor ?? 0) - (item.governmentFeeMinor ?? 0));
  const total = bases.reduce((sum, value) => sum + value, 0);
  let cumulativeBase = 0, allocated = 0;
  return priced.items.map((_, index) => {
    cumulativeBase += bases[index]!;
    const cumulative = total === 0 ? 0 : Number((BigInt(draft.taxMinor) * BigInt(cumulativeBase) + BigInt(Math.floor(total / 2))) / BigInt(total));
    const taxMinor = cumulative - allocated;
    allocated = cumulative;
    return { mode: draft.taxMode, rateBps: draft.taxRateBps, taxMinor, governmentFeeMinor: priced.items[index]!.governmentFeeMinor ?? 0 };
  });
}
